import React, {useEffect, useState} from "react";
import SimpleTable from "../../../common/tables/SimpleTable";
import {fromDate, getRusDateHourString, toDate} from "../../../../utils/dateUtils";
import {useDispatch, useSelector} from "react-redux";
import {loadActionsFilters, loadHistory} from "../../../../actions/historyActions";
import useResizeObserver from "../../../../hooks/useResizeObserver";
import {isDesktop} from "../../../../utils/displayUtils";
import {DESC, LIGHT_THEME, TODAY} from "../../../../utils/constants";
import DateRangePicker from "../../../common/pickers/DateRangePicker";
import {notEmptyString} from "../../../../utils/stringUtils";
import {sort} from "../../../../utils/sortUtils";
import HistoryItemModal from "./HistoryItemModal";
import MaterialUIMultiSelect from "../../../common/selects/MaterialUIMultiSelect";
import useThemeChange from "../../../../hooks/useThemeChange";
import {loadHistoryFiltersUsers} from "../../../../actions/userAction";
import {needToLoadElements} from "../../../../utils/scrollUtils";

const NUMBER_OF_ROWS_TO_RENDER = 50;

export default function HistoryContent({}) {

    const historyItems = useSelector(state => state.historyReducer.historyItems);
    const historyActions = useSelector(state => state.historyReducer.historyActions);
    const historyFiltersUsers = useSelector(state => state.userReducer.historyFiltersUsers);

    const dispatch = useDispatch();
    const [rowIdx, setRowIdx] = useState(NUMBER_OF_ROWS_TO_RENDER);
    const [searchData, setSearchData] = useState({});
    const [selectedFromDate, setSelectedFromDate] = useState(fromDate(TODAY));
    const [selectedToDate, setSelectedToDate] = useState(toDate(TODAY));
    const [sortableColumn, setSortableColumn] = useState(null);
    const [sortableDirection, setSortableDirection] = useState(null);
    const [timeoutState, setTimeoutState] = useState({
        timeout: 0
    });
    const [tableData, setTableData] = useState([]);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [selectedActions, setSelectedActions] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState(null);
    const [selectedForUsers, setSelectedForUsers] = useState(null);

    useEffect(() => {
        dispatch(loadHistory(selectedFromDate, selectedToDate, searchData));
        dispatch(loadActionsFilters());
        dispatch(loadHistoryFiltersUsers());
    }, []);


    useEffect(() => {
        if (!historyItems) {
            return;
        }
        if (timeoutState.timeout) {
            clearTimeout(timeoutState.timeout);
        }
        let sortedTableData = [...historyItems];
        if (notEmptyString(sortableColumn) && notEmptyString(sortableDirection)) {
            const directionNumb = sortableDirection === DESC ? -1 : 1;
            sortedTableData = sort(sortedTableData, sortableColumn, directionNumb);
        }
        sortedTableData = sortedTableData.slice(0, rowIdx);
        setTableData(sortedTableData);
    }, [historyItems, sortableColumn, sortableDirection, rowIdx]);

    useEffect(() => {
        if (timeoutState.timeout) {
            clearTimeout(timeoutState.timeout);
        }
        setTimeoutState({
            timeout: setTimeout(() => {
                const allSearchData = {...searchData,
                    userIds: selectedUsers?.length > 0 && selectedUsers.map(user => user.id),
                    forUserIds: selectedForUsers?.length > 0 && selectedForUsers.map(user => user.id),
                    objectActionIds: selectedActions?.length > 0 && selectedActions.map(action => action.id),
                }
                dispatch(loadHistory(selectedFromDate, selectedToDate, allSearchData, () => setRowIdx(NUMBER_OF_ROWS_TO_RENDER)));
            }, 1000)
        });
    }, [selectedFromDate, selectedToDate, searchData, selectedUsers, selectedForUsers, selectedActions]);

    const setSorting = (column, direction) => {
        setSortableColumn(column);
        setSortableDirection(direction);
    };

    const handleTextTyping = (e) => {
        const fieldName = e.target.name;
        const value = e.target.value;
        setSearchData({...searchData, [fieldName]: value});
    };

    const clearInputs = () => {
        setSelectedFromDate(fromDate(TODAY));
        setSelectedToDate(toDate(TODAY));
        setSearchData({isSystemActionsDisabled: true})
        setSelectedUsers([])
        setSelectedForUsers([])
        setSelectedActions([])
    };

    const handleCloseModal = () => {
        setSelectedItemId(null);
    };

    const handleScroll = (e) => {
        if (!needToLoadElements(e.target, 205)) {
            return;
        }
        setRowIdx(rowIdx + NUMBER_OF_ROWS_TO_RENDER);
    };

    const tableColumns = () => {
        return [
            {
                name: "Дата",
                key: "date",
                getBodyCellValue: item => getRusDateHourString(new Date(item.date)),
                headerCellStyle: {minWidth: "140px", width: "140px"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "Действие",
                key: "message",
                getBodyCellValue: item => item.message,
                headerCellStyle: {minWidth: "200px", width: "25%"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "Автор",
                key: "userName",
                getBodyCellValue: item => item.user?.name,
                headerCellStyle: {minWidth: "200px", width: "15%"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "Аккаунт пользователя",
                key: "forUserName",
                getBodyCellValue: item => item.forUser?.name,
                headerCellStyle: {minWidth: "200px", width: "15%"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "IP",
                key: "ip",
                getBodyCellValue: item => item.ip,
                headerCellStyle: {minWidth: "100px", width: "10%"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "User-Agent",
                key: "userAgent",
                getBodyCellValue: item => item.userAgent,
                headerCellStyle: {minWidth: "250px", width: "30%"},
                bodyCellStyle: {textAlign: "left"},
            },
            {
                name: "",
                key: "actions",
                getBodyCellValue: item => <i className="ti ti-info-alt td-save" onClick={() => setSelectedItemId(item.id)} title='Подробнее'/>,
                headerCellStyle: {minWidth: "80px", width: "80px"},
                isSortingDisabled: true,
            },
        ];
    };

    const themeName = useThemeChange();

    const inputStyles = {
        border: themeName === LIGHT_THEME ? "1px solid #ced4da" : "1px solid #000",
        borderRadius: ".25rem",
        fontSize: '12px !important',
        padding: "0 0 0 5px",
    };

    const multiSelectStyles = {
        root: {
            padding: "0",
            minWidth: 230,
            underlineBorderBottom: '0px solid rgba(0,0,0,0) !important',
        },
        input: {...inputStyles,
            backgroundColor: themeName === LIGHT_THEME ? "white" : "#1c1c1e",
            minHeight: "30px",

        },
        placeholder: {
            fontSize: "12px !important",
            padding: "0 0 3px 5px",
        },
        paper: {
            maxHeight: 300
        },
        chip: {
            maxWidth: '150px',
        },
    };

    const tableActionRow = () => {
        return [
            {
                key: "date",
                getActionCellValue: () =>
                    <div style={{width: "140px", maxWidth: "140px"}}>
                        <DateRangePicker fromDate={selectedFromDate} toDate={selectedToDate}
                                     selectFromDateFunction={setSelectedFromDate} selectToDateFunction={setSelectedToDate} inputType="simple"/>
                    </div>
            },
            {
                key: "message",
                getActionCellValue: () =>
                    <MaterialUIMultiSelect styles={multiSelectStyles}
                                           dropdownData={historyActions}
                                           values={selectedActions}
                                           onSelect={setSelectedActions}
                                           dataField='id'
                                           displayedField='name'
                                           isSearchable
                                           isClearable={true}
                                           maxChipTxtWidth={220}
                                           placeholder="-"
                    />
            },
            {
                key: "userName",
                getActionCellValue: () =>
                    <MaterialUIMultiSelect styles={multiSelectStyles}
                                           dropdownData={historyFiltersUsers}
                                           values={selectedUsers}
                                           onSelect={setSelectedUsers}
                                           dataField='id'
                                           displayedField='name'
                                           isSearchable
                                           isClearable={true}
                                           maxChipTxtWidth={220}
                                           placeholder="-"
                    />
            },
            {
                key: "forUserName",
                getActionCellValue: () =>
                    <MaterialUIMultiSelect styles={multiSelectStyles}
                                           dropdownData={historyFiltersUsers}
                                           values={selectedForUsers}
                                           onSelect={setSelectedForUsers}
                                           dataField='id'
                                           displayedField='name'
                                           isSearchable
                                           isClearable={true}
                                           maxChipTxtWidth={220}
                                           placeholder="-"
                    />
            },
            {
                key: "ip",
                getActionCellValue: () =>
                    <input
                        name="ip"
                        className="form-control "
                        value={searchData["ip"] || ""}
                        onChange={handleTextTyping}/>
            },
            {
                key: "userAgent",
                getActionCellValue: () =>
                    <input
                        name="userAgent"
                        className="form-control "
                        value={searchData["userAgent"] || ""}
                        onChange={handleTextTyping}/>
            },
            {
                key: "actions",
                getActionCellValue: () => <div style={{display: "inline-flex", maxWidth: "80px"}}>
                    <i className="ti ti-reload td-save" onClick={() => dispatch(loadHistory(selectedFromDate, selectedToDate, searchData, () => setRowIdx(NUMBER_OF_ROWS_TO_RENDER)))} title='Обновить'/>
                    <i className="ti ti-close td-save" onClick={clearInputs} title='Очистить'/>
                </div>
            },
        ];
    };

    const height = useResizeObserver(document.getElementById("scrollable")).height - (isDesktop() ? 0 : 50);

    return (
        <div>
            <div className="responsive-block statistic_table_wrapper" id="fullWidth" onScroll={handleScroll}
                 style={{minHeight: '75vh', height: (height + "px")}}>
                <SimpleTable
                    tableClassName="table table-bordered nowrap statistic_table sticky-header sticky-action sticky-footer userselecttext"
                    columns={tableColumns()}
                    data={tableData}
                    bodyRowKey="id"
                    actionRow={tableActionRow()}
                    isSortable
                    sortableColumn={sortableColumn} sortableDirection={sortableDirection} onSort={setSorting}/>
            </div>
            <HistoryItemModal id={selectedItemId} onCloseModal={handleCloseModal}/>
        </div>
    );
}